import { useRef, useState, type ChangeEvent, type DragEvent } from "react";
import { primaryButtonClass, ghostButtonClass } from "@/components/base/fieldStyles";
import { formatNumber } from "@/utils/format";
import { parseBackupJson, type BackupPayload, type RestoreSummary } from "@/utils/jsonBackup";
import RestoreSelectModal from "@/pages/admin/respaldo/components/RestoreSelectModal";

const ImportPanel = () => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [fileName, setFileName] = useState("");
  const [payload, setPayload] = useState<BackupPayload | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [result, setResult] = useState<RestoreSummary | null>(null);

  const reset = () => {
    setFileName("");
    setPayload(null);
    setResult(null);
    setMessage("");
    setError("");
  };

  const handleFile = async (file: File) => {
    setError("");
    setMessage("");
    setResult(null);
    setBusy(true);
    try {
      const parsed = parseBackupJson(await file.text());
      const modules = Object.values(parsed.counts).filter((n) => n > 0).length;
      if (modules === 0) {
        setPayload(null);
        setError("No se detectaron datos reconocibles en el archivo.");
        return;
      }
      setPayload(parsed);
      setFileName(file.name);
      setModalOpen(true);
    } catch (e) {
      setPayload(null);
      setError(e instanceof Error ? e.message : "No se pudo leer el archivo JSON.");
    } finally {
      setBusy(false);
    }
  };

  const onInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) void handleFile(file);
    e.target.value = "";
  };

  const onDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    const file = e.dataTransfer.files?.[0];
    if (file) void handleFile(file);
  };

  const onDone = (summary: RestoreSummary) => {
    setResult(summary);
    setPayload(null);
    setFileName("");
    setMessage("Restauración completada.");
  };

  return (
    <div className="glass-panel rounded-lg p-4 md:p-5">
      <div className="flex items-center gap-2 mb-3">
        <span className="w-8 h-8 rounded-md bg-primary-500/25 text-primary-100 flex items-center justify-center">
          <i className="ri-upload-cloud-2-line" />
        </span>
        <h3 className="editable-title text-base font-bold">Restaurar desde respaldo (.json)</h3>
      </div>
      <p className="text-[11px] text-foreground-500 mb-4">
        Carga un respaldo JSON y elige qué categorías recuperar. La restauración es no destructiva:
        solo <strong className="text-foreground-800">agrega</strong> lo que te falta, nunca borra ni
        sobrescribe lo que ya tienes.
      </p>

      <div
        onDragOver={(e) => e.preventDefault()}
        onDrop={onDrop}
        onClick={() => inputRef.current?.click()}
        className="rounded-lg border-2 border-dashed px-4 py-8 text-center cursor-pointer transition-colors border-background-300/50 hover:border-accent-500/40"
      >
        <input
          ref={inputRef}
          type="file"
          accept=".json,application/json"
          onChange={onInputChange}
          className="hidden"
          aria-label="Seleccionar archivo de respaldo JSON"
        />
        <span className="mx-auto w-12 h-12 rounded-lg bg-background-200/60 text-accent-200 flex items-center justify-center mb-3">
          <i className="ri-file-upload-line text-2xl" />
        </span>
        <p className="text-sm font-semibold text-foreground-900">
          {fileName || "Arrastra tu respaldo aquí o haz clic para seleccionar"}
        </p>
        <p className="text-[11px] text-foreground-500 mt-1">Formato admitido: .json</p>
      </div>

      {busy && (
        <p className="text-[11px] text-accent-200 mt-3 flex items-center gap-1.5">
          <i className="ri-loader-4-line animate-spin" /> Procesando...
        </p>
      )}
      {message && !error && !result && (
        <p className="text-[11px] text-accent-200 mt-3 flex items-center gap-1.5 animate-fade-up">
          <i className="ri-checkbox-circle-line" /> {message}
        </p>
      )}
      {error && (
        <p className="text-[11px] text-primary-200 mt-3 flex items-center gap-1.5 animate-fade-up">
          <i className="ri-error-warning-line" /> {error}
        </p>
      )}

      {result && (
        <div className="mt-4 rounded-md border border-accent-500/30 bg-accent-500/5 px-4 py-3 animate-fade-up">
          <p className="text-sm font-semibold text-foreground-900 flex items-center gap-2">
            <i className="ri-checkbox-circle-line text-accent-300" /> Restauración completada
          </p>
          <p className="text-[11px] text-foreground-600 mt-1">
            Se agregaron <strong className="text-foreground-900">{formatNumber(result.inserted)}</strong>{" "}
            registro(s) · {formatNumber(result.duplicates)} ya existían · {formatNumber(result.skipped)}{" "}
            inválido(s) omitido(s).
          </p>
          <div className="flex flex-wrap gap-1.5 mt-2">
            {result.rows
              .filter((r) => r.inserted > 0 || r.error)
              .map((r) => (
                <span
                  key={r.key}
                  className={`text-[10px] px-2 py-0.5 rounded-full whitespace-nowrap ${
                    r.error
                      ? "bg-primary-500/20 text-primary-100"
                      : "bg-background-200/60 text-foreground-700"
                  }`}
                >
                  {r.label}: {formatNumber(r.inserted)} nuevo(s)
                  {r.error ? " · con error" : ""}
                </span>
              ))}
          </div>
          {result.rows.some((r) => r.error) && (
            <p className="text-[10px] text-primary-200 mt-2">
              Algunas categorías tuvieron errores. Revisa que los datos enlazados (productos,
              clientes) existan e intenta de nuevo.
            </p>
          )}
          <button onClick={reset} className={`${ghostButtonClass} mt-3`}>
            <i className="ri-eraser-line" /> Limpiar resultado
          </button>
        </div>
      )}

      {result && (
        <button onClick={() => setResult(null)} className={`${primaryButtonClass} mt-3`}>
          <i className="ri-upload-cloud-2-line" /> Restaurar otro respaldo
        </button>
      )}

      <RestoreSelectModal
        open={modalOpen}
        payload={payload}
        onClose={() => setModalOpen(false)}
        onDone={onDone}
      />
    </div>
  );
};

export default ImportPanel;